<template>
  <div class="entry-report-page">
    <Button
      v-if="!embedded"
      data-testid="entry-report-back"
      :label="t('Voltar')"
      icon="pi pi-arrow-left"
      severity="secondary"
      text
      size="small"
      class="back-button"
      @click="router.back()"
    />

    <!-- Header -->
    <div v-if="reportData" class="report-header">
      <div class="header-info">
        <h3>{{ t('Laudo de Entrada') }}</h3>
        <div class="item-info">
          <p v-if="reportData.qr_code"><strong>{{ t('QR Code') }}:</strong> {{ reportData.qr_code }}</p>
          <p data-testid="entry-report-item"><strong>{{ t('Item') }}:</strong> {{ reportData.description || '-' }}</p>
          <p data-testid="entry-report-client"><strong>{{ t('Cliente') }}:</strong> {{ reportData.client_name || '-' }}</p>
        </div>
      </div>
      <Tag :value="reportStatusLabel" :severity="reportStatusSeverity" />
    </div>

    <div v-if="isLoading" class="loading-state">
      <ProgressSpinner style="width: 40px; height: 40px;" />
    </div>

    <div v-else-if="reportData">
      <!-- Item Attributes -->
      <box :title="t('Atributos do Item')">
        <template #actions>
          <div class="attributes-edit-toggle">
            <InputToggle
              elem-id="entry_report_attributes_toggle"
              :label="t('Editar')"
              v-model="attributesEditable"
            />
          </div>
        </template>
        <!-- Alertas: atributos boolean agrupados como checkboxes em grid -->
        <div v-if="booleanAttributes.length > 0" class="alertas-section">
          <h4 class="alertas-title">{{ t('Alertas') }}</h4>
          <div class="checkboxes-grid">
            <div
              v-for="attribute in booleanAttributes"
              :key="attribute.attribute_id"
              class="checkbox-item"
            >
              <input-toggle
                :elem-id="`so_entry_report_alerta_${attribute.attribute_id}`"
                :label="attribute.name"
                :model-value="attribute.booleanValue"
                :disabled="!attributesEditable"
                :data-testid="`so-entry-report-alerta-${attribute.attribute_id}`"
                @update:model-value="attribute.booleanValue = $event; attribute.value = $event ? '1' : '0';"
              />
            </div>
          </div>
        </div>

        <!-- Atributos não-booleanos -->
        <div class="attributes-list">
          <div v-for="attribute in nonBooleanAttributes" :key="attribute.attribute_id" class="attribute-item">
            <input-text
              v-if="attribute.type === 'text'"
              :data-testid="`so-entry-report-item-attr-${attribute.attribute_id}`"
              :elem-id="`so_entry_report_attr_text_${attribute.attribute_id}`"
              :label="attribute.name"
              :required="attribute.required"
              :disabled="!attributesEditable"
              :validation="() => isSOEntryReportAttributeValid(attribute)"
              :model-value="String(attribute.value ?? '')"
              @update:model-value="(v?: string) => { attribute.value = v ?? ''; }"
            />

            <input-number
              v-else-if="attribute.type === 'number'"
              :data-testid="`so-entry-report-item-attr-${attribute.attribute_id}`"
              :elem-id="`so_entry_report_attr_number_${attribute.attribute_id}`"
              :label="attribute.name"
              :ng-required="attribute.required"
              :disabled="!attributesEditable"
              :validation="() => isSOEntryReportAttributeValid(attribute)"
              :model-value="attribute.value === '' || attribute.value === null || attribute.value === undefined ? null : Number(attribute.value)"
              @update:model-value="(v: number | null) => (attribute.value = v ?? '')"
            />

            <select-list
              v-else-if="attribute.type === 'select'"
              :data-testid="`so-entry-report-item-attr-${attribute.attribute_id}`"
              :elem-id="`so_entry_report_attr_select_${attribute.attribute_id}`"
              :label="attribute.name"
              :itens-select="attribute.options"
              :ng-required="attribute.required"
              :disabled="!attributesEditable"
              :validation="() => isSOEntryReportAttributeValid(attribute)"
              v-model="attribute.value"
            />
          </div>
        </div>
      </box>

      <!-- Dynamic Report Attributes Section -->
      <SOReportAttributesList
        v-if="reportAttributes.length > 0"
        v-model:attributes="reportAttributes"
        v-model:editable="reportAttributesEditable"
        :order-item-id="effectiveOrderItemId"
        test-id-prefix="so-entry-report-laudo-attr"
      />

      <!-- Customer Requests Verifier Section -->
      <box
        v-if="requests.length > 0"
        :title="t('Solicitações do Cliente')"
        style="margin-top: 16px;"
        data-testid="entry-report-requests-box"
      >
        <SOOrderItemRequestsVerifier
          :requests="requests"
          @request-verified="handleRequestVerified"
        />
      </box>

      <!-- Confirm Button (inline — hidden when embedded; modal footer drives save) -->
      <div v-if="!embedded" class="confirm-section" style="margin-top: 20px;">
        <Button
          :label="isConfirming ? t('Confirmando...') : t('Confirmar Laudo')"
          icon="pi pi-check"
          severity="success"
          :disabled="!canConfirm"
          data-testid="entry-report-confirm-btn"
          @click="handleConfirm"
        />
        <small v-if="hasRequiredReportAttributeErrors" class="confirm-hint">
          {{ t('Preencha todos os atributos obrigatórios do laudo para confirmar') }}
        </small>
      </div>
      <small
        v-else-if="hasRequiredReportAttributeErrors"
        class="confirm-hint"
        data-testid="entry-report-required-hint"
      >
        {{ t('Preencha todos os atributos obrigatórios do laudo para confirmar') }}
      </small>
    </div>

    <div v-else class="empty-state">
      {{ t('Laudo de entrada não encontrado') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useSOEntryReports } from '../../composables/useSOEntryReports';
import { useSOReportAttributeValues } from '../../composables/useSOReportAttributeValues';
import { useSOOrderItemRequests } from '../../composables/useSOOrderItemRequests';
import { useLaudoPublicLink } from '../../composables/useLaudoPublicLink';
import CameraCapture from '../../components/CameraCapture.vue';
import InputToggle from '../../inputs/InputToggle.vue';
import { eventBus } from '../../eventBus';
import { log, error as logError } from '../../utils/DebugLogger';
import { normalizeSOEntryReportAttributes, isSOEntryReportAttributeValid, type SOEntryReportAttribute } from './soEntryReportAttributes';

type Id = number | string;

interface CategoryAttributeBinding {
  attribute_id: Id;
  required?: boolean;
  [key: string]: unknown;
}

interface ReportData {
  order_item_id?: Id;
  status?: string;
  qr_code?: string;
  description?: string;
  client_name?: string;
  attributes?: unknown[];
  category_attributes?: CategoryAttributeBinding[];
  [key: string]: unknown;
}

interface ReportAttributeValue {
  so_rav_attribute_id: Id;
  so_rav_value: string;
}

interface ReportAttributeDefinition {
  so_ra_id: Id;
  so_ra_name?: string;
  so_ra_type?: string;
  [key: string]: unknown;
}

interface ReportAttributeWithValue extends ReportAttributeDefinition {
  value: string | null;
}

interface AttributeDataResponse {
  definitions?: ReportAttributeDefinition[];
  values?: ReportAttributeValue[];
}

interface Props {
  orderItemId?: Id | null;
  embedded?: boolean;
  editableByDefault?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  orderItemId: null,
  embedded: false,
  editableByDefault: false
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { getEntryReport, confirmEntryReport } = useSOEntryReports();
const { fetchLaudoAttributes, saveLaudoAttributeValues } = useSOReportAttributeValues();
const { fetchByOrderItem: fetchRequests } = useSOOrderItemRequests();

const effectiveOrderItemId = computed<Id>(() => (props.orderItemId || route.params.orderItemId) as Id);

const isLoading = ref<boolean>(true);
const isConfirming = ref<boolean>(false);
const isSaving = ref<boolean>(false);
const reportData = ref<ReportData | null>(null);
const editableAttributes = ref<SOEntryReportAttribute[]>([]);
const attributesEditable = ref<boolean>(props.editableByDefault);
const reportAttributesEditable = ref<boolean>(props.editableByDefault);
const reportAttributes = ref<ReportAttributeWithValue[]>([]);
const requests = ref<unknown[]>([]);

const reportStatusLabel = computed<string>(() => {
  if (reportData.value?.status === 'confirmed') return t('Confirmado');
  return t('Pendente');
});

const reportStatusSeverity = computed<string>(() => {
  return reportData.value?.status === 'confirmed' ? 'success' : 'warn';
});

const booleanAttributes = computed<SOEntryReportAttribute[]>(() =>
  editableAttributes.value.filter((a) => a.type === 'boolean' || a.type === 'checkbox')
);

const nonBooleanAttributes = computed<SOEntryReportAttribute[]>(() =>
  editableAttributes.value.filter((a) => a.type !== 'boolean' && a.type !== 'checkbox')
);

const hasRequiredAttributeErrors = computed<boolean>(() =>
  nonBooleanAttributes.value.some((attribute) => attribute.required && !isSOEntryReportAttributeValid(attribute))
);

const hasRequiredReportAttributeErrors = computed<boolean>(() =>
  reportAttributes.value.some((attribute) => {
    const isRequired = attribute.so_cra_required === 1
      || attribute.so_cra_required === '1'
      || attribute.required === 1
      || attribute.required === '1';
    return isRequired && !attribute.value;
  })
);

const allReportAttributesEmpty = computed<boolean>(() =>
  reportAttributes.value.length > 0 && reportAttributes.value.every((attr) => !attr.value)
);

const canConfirm = computed<boolean>(() =>
  !!reportData.value
  && reportData.value.status !== 'confirmed'
  && !hasRequiredAttributeErrors.value
  && !hasRequiredReportAttributeErrors.value
  && !isConfirming.value
);

const canSave = computed<boolean>(() =>
  reportData.value?.status === 'confirmed'
  && (attributesEditable.value || reportAttributesEditable.value)
  && !isSaving.value
);

const loadReport = async (): Promise<void> => {
  const orderItemId = effectiveOrderItemId.value;
  log('SOEntryReportPage', 'loadReport', 'Loading entry report', { orderItemId });
  isLoading.value = true;
  try {
    const [reportData_, attributeData, requestData] = await Promise.all([
      getEntryReport(Number(orderItemId)) as Promise<ReportData | null>,
      fetchLaudoAttributes(orderItemId, 'entry') as unknown as Promise<AttributeDataResponse>,
      fetchRequests(Number(orderItemId)).catch(() => []),
    ]);
    requests.value = requestData || [];

    reportData.value = reportData_ || { order_item_id: orderItemId, status: 'pending' };
    editableAttributes.value = normalizeSOEntryReportAttributes((reportData_?.attributes || []) as never[]);

    // Build report attributes with values
    const definitions = attributeData.definitions || [];
    const values = attributeData.values || [];
    const valueMap: Record<string, string> = Object.fromEntries(values.map((v) => [String(v.so_rav_attribute_id), v.so_rav_value]));

    reportAttributes.value = definitions.map((def): ReportAttributeWithValue => ({
      ...def,
      value: valueMap[String(def.so_ra_id)] || null
    }));

    log('SOEntryReportPage', 'loadReport', 'Loaded report attributes', { count: reportAttributes.value.length });
    eventBus.emit('so.entry-report.loaded', {
      orderItemId,
      status: reportData.value?.status,
      reportAttributeCount: reportAttributes.value.length
    });
  } catch (err: unknown) {
    logError('SOEntryReportPage', 'loadReport', 'Error', err);
    reportData.value = { order_item_id: orderItemId, status: 'pending' };
    editableAttributes.value = [];
    reportAttributes.value = [];
    eventBus.emit('so.entry-report.loaded', { orderItemId, status: 'pending', reportAttributeCount: 0, error: true });
  } finally {
    isLoading.value = false;
  }
};

const handleConfirm = async (): Promise<void> => {
  const orderItemId = effectiveOrderItemId.value;
  log('SOEntryReportPage', 'handleConfirm', 'Confirming entry report', { orderItemId });
  isConfirming.value = true;

  try {
    // Save report attributes first
    const reportAttributeValues = reportAttributes.value
      .filter((attr) => attr.value !== null && attr.value !== undefined)
      .map((attr) => ({
        attribute_id: attr.so_ra_id,
        value: attr.value
      }));

    if (reportAttributeValues.length > 0) {
      await saveLaudoAttributeValues(orderItemId, reportAttributeValues, 'entry');
      log('SOEntryReportPage', 'handleConfirm', 'Saved report attributes', { count: reportAttributeValues.length });
    }

    // Then confirm the report
    await confirmEntryReport(orderItemId, editableAttributes.value as never);
    if (reportData.value) {
      reportData.value.status = 'confirmed';
    }
    eventBus.emit('globalMessage.success', t('Laudo de entrada confirmado'));
    eventBus.emit('so-orders.saved');
    eventBus.emit('modal.pdv-laudos-entrada.close');

    // Oferecer-ao-concluir: gera o link público e abre o share por WhatsApp.
    // offerLaudoLink engole erros — nunca bloqueia a confirmação.
    const { offerLaudoLink } = useLaudoPublicLink();
    await offerLaudoLink(Number(orderItemId), 'entrada', reportData.value?.client_name ?? '');
  } catch (err: unknown) {
    logError('SOEntryReportPage', 'handleConfirm', 'Error', err);
    const message = err instanceof Error ? err.message : String(err);
    eventBus.emit('globalMessage.error', t('Erro ao confirmar laudo') + ': ' + message);
  } finally {
    isConfirming.value = false;
  }
};

const handleSave = async (): Promise<void> => {
  const orderItemId = effectiveOrderItemId.value;
  log('SOEntryReportPage', 'handleSave', 'Saving laudo attributes', { orderItemId });
  isSaving.value = true;

  try {
    const reportAttributeValues = reportAttributes.value
      .filter((attr) => attr.value !== null && attr.value !== undefined)
      .map((attr) => ({
        attribute_id: attr.so_ra_id,
        value: attr.value
      }));

    if (reportAttributeValues.length > 0) {
      await saveLaudoAttributeValues(orderItemId, reportAttributeValues, 'entry');
    }
    eventBus.emit('globalMessage.success', t('Atributos do laudo salvos'));
    eventBus.emit('so-orders.saved');
  } catch (err: unknown) {
    logError('SOEntryReportPage', 'handleSave', 'Error', err);
    const message = err instanceof Error ? err.message : String(err);
    eventBus.emit('globalMessage.error', t('Erro ao salvar') + ': ' + message);
  } finally {
    isSaving.value = false;
  }
};

onMounted(() => {
  loadReport();
});

async function handleRequestVerified(): Promise<void> {
  try {
    const updatedRequests = await fetchRequests(Number(effectiveOrderItemId.value));
    requests.value = updatedRequests || [];
  } catch (err) {
    logError('SOEntryReportPage', 'handleRequestVerified', 'Error refreshing requests', err);
  }
  eventBus.emit('so-order-item.updated', { itemId: Number(effectiveOrderItemId.value) });
}

defineExpose({ handleConfirm, canConfirm, isConfirming, handleSave, canSave, isSaving });
</script>

<style scoped>
.entry-report-page {
  padding: 16px;
  
}

.back-button {
  margin-bottom: 8px;
}

.report-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  background: var(--p-surface-100);
  border-radius: 8px;
  margin-bottom: 16px;
}

.header-info h3 {
  margin: 0 0 8px 0;
}

.item-info p {
  margin: 2px 0;
  font-size: 0.875rem;
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 40px;
}

.attributes-edit-toggle {
  display: flex;
  align-items: center;
}

.attributes-edit-toggle :deep(.toggle-container) {
  margin: 0;
  gap: 8px;
}

.attribute-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--p-text-color);
}

.required-indicator {
  color: var(--p-red-500);
  margin-right: 4px;
}

/* Alertas checkbox grid */
.alertas-section {
  margin-bottom: 16px;
}

.alertas-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--p-text-color);
  margin: 0 0 12px 0;
}

.checkboxes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 8px 16px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.boolean-attribute {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.boolean-label {
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--p-text-color);
}

.boolean-control {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.boolean-control :deep(.toggle-container) {
  margin: 0;
}

.boolean-control :deep(.toggle-label) {
  display: none;
}

.boolean-value {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--p-red-400);
  min-width: 28px;
  text-align: right;
}

.boolean-value.active {
  color: var(--p-green-500);
}

.photos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
}

.photo-progress {
  margin-top: 12px;
  font-size: 0.85rem;
  color: var(--p-text-muted-color);
  text-align: center;
}

.confirm-section {
  text-align: center;
}

.confirm-hint {
  display: block;
  margin-top: 8px;
  color: var(--p-text-muted-color);
  font-style: italic;
}

.empty-state {
  color: var(--p-text-muted-color);
  font-style: italic;
  padding: 40px;
  text-align: center;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-field label {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--p-text-color);
}

</style>
